// Utility function for handling representative images

// Default avatar SVG (person icon) - small size for duplicates view
export const DEFAULT_AVATAR_SMALL = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjgwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik00MCA0NkM0OC4yODQzIDQ2IDU1IDM5LjI4NDMgNTUgMzFDNTUgMjIuNzE1NyA0OC4yODQzIDE2IDQwIDE2QzMxLjcxNTcgMTYgMjUgMjIuNzE1NyAyNSAzMUMyNSAzOS4yODQzIDMxLjcxNTcgNDYgNDAgNDZaIiBmaWxsPSIjQ0NDQ0NDIi8+CjxwYXRoIGQ9Ik00MCA1MEMzMC4wNSA1MCAyMS43NSA1Ni4yNSAyMSA2NEg1OUM1OC4yNSA1Ni4yNSA0OS45NSA1MCA0MCA1MFoiIGZpbGw9IiNDQ0NDQ0MiLz4KPC9zdmc+';

// Default avatar SVG (person icon) - larger size for main view
export const DEFAULT_AVATAR_LARGE = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTMwIiBoZWlnaHQ9IjEzMCIgdmlld0JveD0iMCAwIDEzMCAxMzAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMzAiIGhlaWdodD0iMTMwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik02NSA3NkM3Ni4wNDU3IDc2IDg1IDY3LjA0NTcgODUgNTZDODUgNDQuOTU0MyA3Ni4wNDU3IDM2IDY1IDM2QzUzLjk1NDMgMzYgNDUgNDQuOTU0MyA0NSA1NkM0NSA2Ny4wNDU3IDUzLjk1NDMgNzYgNjUgNzZaIiBmaWxsPSIjQ0NDQ0NDIi8+CjxwYXRoIGQ9Ik02NSA4NEM0Ny41IDg0IDMzIDkzIDMzIDEwNEg5N0M5NyA5MyA4Mi41IDg0IDY1IDg0WiIgZmlsbD0iI0NDQ0NDQyIvPgo8L3N2Zz4K';

/**
 * Get representative image URL with fallback to default avatar
 * @param {string} imagePath - The base64 image path from the representative data
 * @param {string} size - 'small' or 'large' for different default avatar sizes
 * @returns {string} - Image URL or default avatar
 */
export const getRepresentativeImageUrl = (imagePath, size = 'large') => {
  // Check if imagePath exists and is not empty
  if (imagePath && imagePath.trim() !== '' && imagePath !== 'base64imagestring1' && 
      imagePath !== 'base64imagestring2' && imagePath !== 'base64imagestring3' &&
      imagePath !== 'base64imagestring4' && imagePath !== 'base64imagestring5' &&
      imagePath !== 'base64imagestring6' && imagePath !== 'base64imagestring7' &&
      imagePath !== 'base64imagestring8' && imagePath !== 'base64imagestring9' &&
      imagePath !== 'base64imagestring10') {
    return `data:image/jpeg;base64,${imagePath}`;
  }
  
  // Return appropriate default avatar based on size
  return size === 'small' ? DEFAULT_AVATAR_SMALL : DEFAULT_AVATAR_LARGE;
};

/**
 * Handle image error by setting default avatar
 * @param {Event} event - The error event
 * @param {string} size - 'small' or 'large' for different default avatar sizes
 */
export const handleImageError = (event, size = 'large') => {
  event.target.src = size === 'small' ? DEFAULT_AVATAR_SMALL : DEFAULT_AVATAR_LARGE;
};

/**
 * Check if an image path is valid (not a placeholder or empty)
 * @param {string} imagePath - The image path to validate
 * @returns {boolean} - True if the image path appears to be valid
 */
export const isValidImagePath = (imagePath) => {
  if (!imagePath || imagePath.trim() === '') {
    return false;
  }
  
  // Check for placeholder values
  const placeholders = [
    'base64imagestring1', 'base64imagestring2', 'base64imagestring3',
    'base64imagestring4', 'base64imagestring5', 'base64imagestring6',
    'base64imagestring7', 'base64imagestring8', 'base64imagestring9',
    'base64imagestring10'
  ];
  
  return !placeholders.includes(imagePath.trim());
};